import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isReserveTicket, showNotificationDialog } from '../utils';
import { TIMEOUT_DURATION, localStorageKeys } from '../utils/Constants';
import useR05Api from '../api/useR05Api';
import { LocalStorageManager } from '../utils/LocalStorageManager/LocalStorageManager';
import { TicketInfo } from 'types';
import { ErrorType } from 'types/errors';

/**
 * チケット購入画面表示の部分
 */
const useTicketInfo = () => {
    const [ticketInfo, setTicketInfo] = useState<TicketInfo>();
    const [isLoading, setLoading] = useState(false);
    const { t } = useTranslation();
    const { fetchTicketInfo } = useR05Api();
    const navigate = useNavigate();
    const storageManager = useMemo(() => new LocalStorageManager('useTicketInfo'), []);

    const showFetchTicketInfoError = useCallback(
        (error: ErrorType) => {
            const showError = (text: string) => {
                showNotificationDialog('error', 'fetchTicketInfo', t('notification.title'), text);
            };
            switch (error.errorType) {
                case 'offline':
                case 'timeout':
                    showError(t('error-message.server-error'));
                    break;
                case 'undefined_parameter':
                case 'not_found_ticket':
                case 'invalid_api_key':
                    showError(t('error-message.tickets.fetch-info-failed'));
                    break;
                case 'unexpected':
                default:
                    showError(t('error-message.tickets.unexpected-error'));
                    break;
            }
        },
        [t],
    );

    // チケット情報の取得
    const fetch = useCallback(
        async (salesTicketId: string) => {
            setLoading(true);
            let fetchTimerId: NodeJS.Timeout | undefined = undefined;
            try {
                if (!navigator.onLine) {
                    setLoading(false);
                    showFetchTicketInfoError({ errorType: 'offline' });
                    return;
                }

                fetchTimerId = setTimeout(() => {
                    setLoading(false);
                    if (fetchTimerId !== undefined) {
                        clearTimeout(fetchTimerId);
                    }
                    showFetchTicketInfoError({ errorType: 'timeout' });
                }, TIMEOUT_DURATION);

                const data: TicketInfo | undefined = await fetchTicketInfo({ salesTicketId: salesTicketId });
                clearTimeout(fetchTimerId);
                if (data === null || data === undefined) {
                    return;
                }
                const ticketInfo = {
                    ...data,
                    isReserve: isReserveTicket(data),
                };
                storageManager.setStorage(localStorageKeys.TICKET_INFO_DATA, ticketInfo);

                setTicketInfo(ticketInfo);
                return ticketInfo;
            } catch (error: any) {
                showFetchTicketInfoError(error);
                navigate('/buy');
                clearTimeout(fetchTimerId);
            } finally {
                setLoading(false);
            }
        },
        [fetchTicketInfo, navigate, showFetchTicketInfoError, storageManager],
    );

    return {
        isLoading,
        ticketInfo,
        fetchTicketInfo: fetch,
    };
};

export default useTicketInfo;
